/**
 * =============================================================================
 * IDS CRM Web - Quote Detail (Phase 4)
 * =============================================================================
 * Read view of a quote: grouped product + non-asset lines, subtotal, status,
 * the excludes-tax statement, send action, and per-line accept/reject.
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatMoney } from '../utils/money';
import { formatDateKey } from '../utils/dates';
import { openDraft } from '../utils/emailDraft';
import EmailDraftModal from '../components/EmailDraftModal';
import './QuoteDetail.css';
import './CommercialLayout.css';

const DOC_TYPE_TITLES = { contract: 'Contract', quote: 'Quote', acknowledgement: 'Order acknowledgement' };

/**
 * Email-to-customer on the shared EmailDraftModal (R4c, §0.8): pick a contact,
 * the server builds the draft, the document(s) download for manual attachment
 * (mailto can't attach), then the user's own client opens with the CRM inbox
 * CC'd. Only logged once the draft actually opened.
 */
function QuoteEmailModal({ quote, documents, authFetch, onClose, onLogged }) {
  const [includePack, setIncludePack] = useState(false);

  const download = async (url, name) => {
    const response = await authFetch(url);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Document download failed');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  };

  return (
    <EmailDraftModal
      title="Email to Customer"
      customerId={quote.customer_id}
      authFetch={authFetch}
      onClose={onClose}
      preferEmailContact
      actionLabel="Download & Open Draft"
      onAction={async (contactId) => {
        const draftRes = await authFetch(`/api/v1/quotes/${quote.quote_id}/email-draft?contact_id=${contactId}`);
        const draftData = await draftRes.json();
        if (!draftRes.ok) throw new Error(draftData.message || 'Failed to build the draft');

        // Download the document(s) for manual attachment (mailto can't attach).
        const latest = documents[0];
        if (latest) {
          await download(`/api/v1/quotes/${quote.quote_id}/documents/${latest.quote_document_id}/pdf`, `${quote.quote_ref}-v${latest.version}.pdf`);
        } else {
          await download(`/api/v1/quotes/${quote.quote_id}/pdf`, `${quote.quote_ref}.pdf`);
        }
        if (includePack) {
          await download(`/api/v1/quotes/${quote.quote_id}/pack?partial=true`, `Contract Pack - ${quote.group_ref || quote.quote_ref}.pdf`);
        }

        openDraft(draftData.draft);
        await authFetch(`/api/v1/quotes/${quote.quote_id}/email-logged`, {
          method: 'POST',
          body: JSON.stringify({ contact_id: contactId })
        });
        onLogged();
      }}
    >
      <label className="checkbox-label">
        <input type="checkbox" checked={includePack} onChange={(e) => setIncludePack(e.target.checked)} />
        <span>Also download the Contract Pack (T&amp;Cs + guides)</span>
      </label>
      <p className="line-sub" style={{ marginTop: 10 }}>
        This opens a pre-filled draft in <strong>your own email client</strong> with the CRM inbox
        CC'd (so the copy and any replies are captured), and downloads the document(s) —
        <strong> attach the downloaded file(s) before sending</strong>.
      </p>
    </EmailDraftModal>
  );
}

const STATUS_LABELS = {
  draft: 'Draft', sent: 'Sent', accepted: 'Accepted',
  partially_accepted: 'Partially accepted', rejected: 'Rejected',
  expired: 'Expired', withdrawn: 'Withdrawn'
};

function QuoteDetail() {
  const { id } = useParams();
  const { authFetch } = useAuth();
  const [quote, setQuote] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const fetchQuote = useCallback(async () => {
    setLoading(true);
    try {
      const [quoteRes, docsRes] = await Promise.all([
        authFetch(`/api/v1/quotes/${id}`),
        authFetch(`/api/v1/quotes/${id}/documents`)
      ]);
      if (quoteRes.ok) setQuote(await quoteRes.json());
      else setError('Failed to load quote');
      if (docsRes.ok) setDocuments((await docsRes.json()).data || []);
    } catch (err) {
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const generateDocument = async () => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/generate-document`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to generate the document');
      fetchQuote();
    } catch (err) { setError(err.message); setBusy(false); }
    finally { setBusy(false); }
  };

  const openBlob = async (url, downloadName) => {
    setError('');
    try {
      const res = await authFetch(url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to fetch the document');
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (downloadName) {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = downloadName;
        link.click();
      } else {
        window.open(objectUrl, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) { setError(err.message); }
  };

  const downloadPack = () => openBlob(`/api/v1/quotes/${id}/pack`, `Contract Pack - ${quote?.group_ref || quote?.quote_ref}.pdf`);

  const changeStatus = async (status) => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update status');
      setQuote(data);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const openPdf = async () => {
    setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/pdf`);
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err.message); }
  };

  const sendForSignature = async () => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/send-for-signature`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send for signature');
      setQuote(data);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const simulateSigning = async () => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/esign/complete`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to complete signature');
      setQuote(data);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const setItemStatus = async (itemId, item_status) => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/v1/quotes/${id}/items/${itemId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update line');
      setQuote(data);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="quote-detail-page"><p>Loading…</p></div>;
  if (!quote) return <div className="quote-detail-page"><p className="modal-error">{error || 'Quote not found'}</p></div>;

  const currency = quote.currency;

  return (
    <div className="quote-detail-page">
      <div className="page-header">
        <div>
          <h1>{quote.quote_ref} <span className="line-sub">{DOC_TYPE_TITLES[quote.doc_type || 'contract']}</span>
            {quote.repair_replace && <span className="line-sub"> · repair &amp; replace policy (balance due 0)</span>}</h1>
          <p>
            <Link to={`/customers/${quote.customer_id}`}>{quote.customer_name}</Link>
            {quote.opportunity_group_id && (
              <> {' · '}<Link to={`/pipeline/groups/${quote.opportunity_group_id}`}>{quote.group_ref} — {quote.group_title}</Link></>
            )}
            {' · '}<span className={`quote-status status-${quote.status}`}>{STATUS_LABELS[quote.status] || quote.status}</span>
            {quote.is_contract && <> {' · '}<span className="contract-badge">✔ Contract</span></>}
            {!quote.is_contract && quote.esign_status === 'sent' && <> {' · '}<span className="esign-badge">Awaiting signature</span></>}
            {quote.valid_until && <> · valid until {formatDateKey(quote.valid_until)}</>}
          </p>
        </div>
        <div className="quote-actions">
          <button type="button" className="btn-secondary" onClick={openPdf} title="Opens the branded document as the customer sees it">
            Preview PDF
          </button>
          {quote.opportunity_group_id && (
            <button type="button" className="btn-secondary" disabled={busy} onClick={downloadPack} title="Contract + T&Cs + pre-install guide, merged">
              Contract Pack
            </button>
          )}
          <button type="button" className="btn-primary" disabled={busy} onClick={() => setShowEmailModal(true)}
            title="Opens your own email client pre-filled (CRM inbox CC'd) and downloads the document">
            Email to Customer
          </button>
          {quote.status === 'draft' && (
            <>
              <Link to={`/quotes/${id}/edit`} className="btn-secondary">Edit</Link>
              <button className="btn-primary" disabled={busy} onClick={() => changeStatus('sent')}>Send</button>
            </>
          )}
          {/* R3 (DL-9): e-sign is dormant until enabled in Settings → Integrations */}
          {quote.esign_enabled && ['sent', 'partially_accepted'].includes(quote.status) && quote.esign_status !== 'sent' && quote.esign_status !== 'completed' && (
            <button className="btn-primary" disabled={busy} onClick={sendForSignature}>Send for signature</button>
          )}
          {quote.esign_enabled && quote.esign_status === 'sent' && !quote.is_contract && (
            <button className="btn-secondary" disabled={busy} onClick={simulateSigning} title="Dev stub: simulate the customer signing">Simulate signing (dev)</button>
          )}
          {['sent', 'partially_accepted'].includes(quote.status) && !quote.is_contract && (
            <button className="btn-secondary" disabled={busy} onClick={() => changeStatus('withdrawn')}>Withdraw</button>
          )}
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}

      <table className="data-table quote-lines">
        <thead>
          <tr>
            <th>Line</th><th>Type</th><th className="num">Qty</th><th className="num">Unit price</th><th className="num">Line total</th>
            {['sent', 'partially_accepted'].includes(quote.status) && <th>Decision</th>}
          </tr>
        </thead>
        <tbody>
          {quote.items.map(item => {
            const lineTotal = (Number(item.unit_price_minor_units) || 0) * (Number(item.quantity) || 0);
            return (
              <tr key={item.quote_item_id} className={`item-${item.item_status}`}>
                <td>
                  <div className="line-title">{item.title}</div>
                  {item.opp_ref && <div className="line-sub">{item.opp_ref} · {item.opportunity_stage}</div>}
                  {item.description && <div className="line-sub">{item.description}</div>}
                </td>
                <td>{item.line_type === 'product' ? 'Product' : 'Other'}</td>
                <td className="num">{item.quantity}</td>
                <td className="num">{formatMoney(item.unit_price_minor_units, currency)}</td>
                <td className="num">{formatMoney(lineTotal, currency)}</td>
                {['sent', 'partially_accepted'].includes(quote.status) && (
                  <td>
                    {item.line_type === 'product' ? (
                      <span className="line-decision">
                        <button className={`chip ${item.item_status === 'accepted' ? 'on' : ''}`} disabled={busy} onClick={() => setItemStatus(item.quote_item_id, 'accepted')}>Accept</button>
                        <button className={`chip danger ${item.item_status === 'rejected' ? 'on' : ''}`} disabled={busy} onClick={() => setItemStatus(item.quote_item_id, 'rejected')}>Reject</button>
                      </span>
                    ) : <span className="line-sub">—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} className="num subtotal-label">Subtotal</td>
            <td className="num subtotal">{formatMoney(quote.subtotal_minor_units, currency)}</td>
            {['sent', 'partially_accepted'].includes(quote.status) && <td></td>}
          </tr>
          {Number(quote.monthly_minor_units) > 0 && (
            <tr>
              <td colSpan={4} className="num subtotal-label">Monthly total</td>
              <td className="num subtotal">{formatMoney(quote.monthly_minor_units, currency)}</td>
              {['sent', 'partially_accepted'].includes(quote.status) && <td></td>}
            </tr>
          )}
        </tfoot>
      </table>

      <p className="excludes-tax">All prices are in {currency} and <strong>exclude tax, tariffs and shipping</strong>.</p>

      {quote.notes && <div className="quote-notes"><strong>Notes:</strong> {quote.notes}</div>}

      <div className="quote-documents-section">
        {quote.pricing_out_of_date && (
          <div className="form-error" role="alert">
            Pricing has changed since document v{quote.latest_document_version} (e.g. an approved
            contract variation) — regenerate to issue the new version.
          </div>
        )}
        <div className="section-head">
          <h2>Generated documents</h2>
          <button className="btn-secondary btn-small" disabled={busy} onClick={generateDocument} title="Render the current wording + pricing into a new stored version">
            {documents.length === 0 ? 'Generate PDF' : 'Regenerate PDF'}
          </button>
        </div>
        {documents.length === 0 ? (
          <p className="line-sub">No stored versions yet — Generate PDF stores version 1. Wording edits + regenerate create the next version (full document trail).</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Version</th><th>Type</th><th>Generated</th><th>By</th><th></th></tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.quote_document_id}>
                  <td>v{doc.version}</td>
                  <td>{DOC_TYPE_TITLES[doc.doc_type || 'contract']}</td>
                  <td>{new Date(doc.generated_at).toLocaleString('en-GB')}</td>
                  <td>{doc.generated_by_name || '—'}</td>
                  <td>
                    <button className="btn-link" onClick={() => openBlob(`/api/v1/quotes/${id}/documents/${doc.quote_document_id}/pdf`)}>
                      View PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="back-link"><Link to={`/customers/${quote.customer_id}`}>← {quote.customer_name}</Link></div>

      {showEmailModal && (
        <QuoteEmailModal
          quote={quote}
          documents={documents}
          authFetch={authFetch}
          onClose={() => setShowEmailModal(false)}
          onLogged={() => { setShowEmailModal(false); fetchQuote(); }}
        />
      )}
    </div>
  );
}

export default QuoteDetail;
